'use client'

import { memo } from 'react'
import Link from 'next/link'
import { motion, useReducedMotion } from 'framer-motion'
import { Landmark, Lock, ShieldCheck, Vault } from 'lucide-react'
import { cn } from '@/lib/utils'
import { NinxlScrollReveal } from './ninxl-scroll-reveal'

export const NINX_BANKS_SALES_EMAIL = 'banks@nin.in'

const CORE_LINES = [
  'CORE://NINX/BANKS. SESSION SECURED',
  'FRAUD GATE: ARMED',
  'PCI SCOPE: ON-PREM ONLY',
  'SETTLEMENT QUEUE: 4 TXNS LOCAL',
] as const

const LIVE_TXNS = [
  { id: 'TXN-8841', type: 'DEBIT', amount: '₹42,500', status: 'CLEARED' },
  { id: 'TXN-8842', type: 'CREDIT', amount: '₹1,20,000', status: 'VERIFIED' },
  { id: 'TXN-8843', type: 'DEBIT', amount: '₹8,750', status: 'FRAUD OK' },
  { id: 'TXN-8844', type: 'CREDIT', amount: '₹3,05,000', status: 'POSTED' },
] as const

const COMPLIANCE_CHIPS = [
  { label: 'PCI-DSS', value: 'IN SCOPE' },
  { label: 'SOX', value: 'AUDIT READY' },
  { label: 'SOC 2', value: 'IN PROGRESS' },
  { label: 'CASA', value: 'CERTIFIED' },
  { label: 'ZDR', value: 'CLOUD CREDITS' },
] as const

const SETTLEMENT_DOTS = [
  { x1: 48, y1: 168, x2: 48, y2: 24, delay: 0 },
  { x1: 144, y1: 168, x2: 144, y2: 24, delay: 0.6 },
  { x1: 240, y1: 168, x2: 240, y2: 24, delay: 1.2 },
] as const

/** Pixel vault + live settlement ticker */
const NinxlBanksVaultTicker = memo(function NinxlBanksVaultTicker() {
  const shouldReduce = useReducedMotion()

  return (
    <motion.div
      className="ninxl-banks-vault ninxl-pixel-border relative mx-auto w-full max-w-md p-4 md:p-5"
      initial={shouldReduce ? false : { opacity: 0, scale: 0.96 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
    >
      <div className="ninxl-8bit-label ninxl-banks-vault-title mb-3 flex items-center justify-between">
        <span>SECURE VAULT LEDGER</span>
        <span className="ninxl-banks-sync-dot" aria-hidden />
      </div>

      <svg viewBox="0 0 320 180" className="ninxl-banks-vault-svg mb-4 h-auto w-full" aria-hidden>
        <rect x="0" y="0" width="320" height="180" fill="var(--ninxl-theme-panel, #fff)" fillOpacity="0.03" />
        <rect x="88" y="48" width="144" height="108" fill="#134b5f" fillOpacity="0.14" stroke="#21728d" strokeWidth="2" />
        <rect x="108" y="68" width="104" height="68" fill="#21728d" fillOpacity="0.22" stroke="#21728d" strokeWidth="2" />
        <g transform="translate(160 102)">
          <circle r="18" fill="none" stroke="#21728d" strokeWidth="3">
            {!shouldReduce ? (
              <animateTransform attributeName="transform" type="rotate" from="0" to="360" dur="12s" repeatCount="indefinite" />
            ) : null}
          </circle>
          <rect
            x="-8"
            y="-8"
            width="16"
            height="16"
            fill="#21728d"
            fillOpacity="0.5"
            className={shouldReduce ? undefined : 'ninxl-banks-vault-lock'}
          />
        </g>
        <rect x="124" y="148" width="72" height="8" fill="#88a1ac" fillOpacity="0.4" className="ninxl-banks-vault-base" />
        {[48, 96, 144, 192, 240].map((x) => (
          <line key={x} x1={x} y1="24" x2={x} y2="168" stroke="#21728d" strokeWidth="1" strokeOpacity="0.08" />
        ))}
        {!shouldReduce
          ? SETTLEMENT_DOTS.map((dot) => (
              <circle key={dot.x1} r="3" fill="#10b981" className="ninxl-banks-settlement-dot" style={{ animationDelay: `${dot.delay}s` }}>
                <animateMotion
                  dur="2.8s"
                  repeatCount="indefinite"
                  path={`M ${dot.x1} ${dot.y1} L ${dot.x2} ${dot.y2}`}
                  calcMode="linear"
                  begin={`${dot.delay}s`}
                />
              </circle>
            ))
          : null}
        <text x="160" y="36" textAnchor="middle" className="fill-[#5f7380] text-[8px] font-bold tracking-[0.12em]">
          ON-PREM SETTLEMENT LAYER
        </text>
      </svg>

      <div className="space-y-2">
        {LIVE_TXNS.map((txn, index) => (
          <motion.div
            key={txn.id}
            className={cn('ninxl-banks-txn-row flex items-center justify-between gap-2 px-2 py-1.5', !shouldReduce && 'ninxl-banks-txn-row--cycle')}
            style={shouldReduce ? undefined : { animationDelay: `${index * 2}s` }}
            initial={shouldReduce ? false : { opacity: 0, x: 16 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3 + index * 0.12, duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="min-w-0">
              <span className="ninxl-8bit-label block text-[9px]">{txn.id}</span>
              <span
                className={cn(
                  'ninxl-banks-txn-type text-[10px] font-bold',
                  txn.type === 'DEBIT' ? 'ninxl-banks-txn-type--debit' : 'ninxl-banks-txn-type--credit',
                )}
              >
                {txn.type} · {txn.amount}
              </span>
            </div>
            <span className="ninxl-banks-txn-status">{txn.status}</span>
          </motion.div>
        ))}
      </div>
    </motion.div>
  )
})

/** Banking hero: core terminal + vault ticker */
export const NinxlBanksHero = memo(function NinxlBanksHero({ className }: { className?: string }) {
  const shouldReduce = useReducedMotion()

  return (
    <section data-section="banks-hero" className={cn('relative pt-12 pb-14 md:pt-16 md:pb-20', className)}>
      <div className="mx-auto grid max-w-6xl items-center gap-10 px-6 lg:grid-cols-[1fr_1fr] lg:gap-12">
        <NinxlScrollReveal variant="left">
          <div className="ninxl-banks-core-terminal ninxl-pixel-border ninxl-banks-terminal-glow relative mb-6 overflow-hidden p-4 md:p-5">
            <div className="ninxl-8bit-label mb-3 flex items-center gap-2">
              <Vault className="h-3.5 w-3.5" aria-hidden />
              BANKING CORE TERMINAL
            </div>
            <div className="space-y-1.5 font-mono text-sm md:text-base">
              {CORE_LINES.map((line, index) => (
                <motion.div
                  key={line}
                  className="ninxl-banks-core-line overflow-hidden whitespace-nowrap"
                  initial={shouldReduce ? false : { opacity: 0, x: -8 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.15 + index * 0.22, duration: 0.35 }}
                >
                  <span className={!shouldReduce ? 'ninxl-banks-typewriter-line' : undefined} style={{ animationDelay: `${0.15 + index * 0.22}s` }}>
                    &gt; {line}
                  </span>
                </motion.div>
              ))}
              {!shouldReduce ? (
                <div className="ninxl-banks-core-line ninxl-banks-terminal-cursor font-mono text-sm md:text-base">
                  &gt; <span className="ninxl-banks-cursor-blink">_</span>
                </div>
              ) : null}
            </div>
          </div>

          <p className="ninxl-8bit-label ninxl-theme-text-accent mb-3 tracking-[0.18em]">REGULATED FINANCIAL AI</p>
          <h1 className="ninxl-8bit-heading ninxl-theme-text mb-5 text-left text-xl leading-tight sm:text-2xl md:text-3xl">
            AI inside the
            <br />
            <span className="ninxl-theme-text-muted">banking perimeter.</span>
          </h1>

          <p className="ninxl-8bit-body mb-8 max-w-xl text-left text-base md:text-lg">
            NINX Banks runs on infrastructure you control: screening fraud, posting to core ledger,
            and preserving auditor-ready trails without routing PANs through foreign clouds.
          </p>

          <div className="mb-8 flex flex-wrap gap-3">
            <Link
              href={`mailto:${NINX_BANKS_SALES_EMAIL}?subject=NINX%20Banks%20Partnership%20Inquiry`}
              className="ninxl-pixel-btn ninxl-pixel-btn-primary"
            >
              Talk to Banking Team
            </Link>
            <a href="#banks-matrix" className="ninxl-pixel-btn">
              View regulatory fit
            </a>
          </div>

          <div className="flex flex-wrap gap-2">
            {COMPLIANCE_CHIPS.map((chip, index) => (
              <motion.div
                key={chip.label}
                className="ninxl-banks-compliance-chip ninxl-banks-compliance-chip--float px-3 py-2"
                style={{ animationDelay: `${index * 0.35}s` }}
                initial={shouldReduce ? false : { opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.9 + index * 0.1, duration: 0.4 }}
                whileHover={shouldReduce ? undefined : { y: -3, transition: { duration: 0.2 } }}
              >
                <div className="ninxl-8bit-label text-[8px]">{chip.label}</div>
                <div className="ninxl-theme-text-accent text-[10px] font-bold">{chip.value}</div>
              </motion.div>
            ))}
          </div>
        </NinxlScrollReveal>

        <NinxlScrollReveal variant="right" delay={0.1}>
          <div className="relative">
            <motion.div
              className="ninxl-banks-hero-badge absolute -left-2 -top-2 z-10 hidden items-center gap-1.5 px-2 py-1 md:flex"
              animate={shouldReduce ? undefined : { y: [0, -4, 0] }}
              transition={{ duration: 3.2, repeat: Infinity, ease: 'easeInOut' }}
            >
              <ShieldCheck className="h-3.5 w-3.5" aria-hidden />
              <span className="ninxl-8bit-label text-[9px]">PCI BOUNDARY</span>
            </motion.div>
            <motion.div
              className="ninxl-banks-hero-badge absolute -bottom-2 -right-2 z-10 flex items-center gap-1.5 px-2 py-1"
              animate={shouldReduce ? undefined : { y: [0, 4, 0] }}
              transition={{ duration: 3.6, repeat: Infinity, ease: 'easeInOut', delay: 0.4 }}
            >
              <Lock className="h-3.5 w-3.5" aria-hidden />
              <span className="ninxl-8bit-label text-[9px]">CORE CONNECTED</span>
            </motion.div>
            <NinxlBanksVaultTicker />
          </div>
        </NinxlScrollReveal>
      </div>

      <div className="ninxl-8bit-label ninxl-theme-text-muted mx-auto mt-10 flex max-w-6xl items-center justify-center gap-2 px-6 text-center text-[10px]">
        <Landmark className="h-3.5 w-3.5" aria-hidden />
        Built for retail, investment, insurance, and payments institutions
      </div>
    </section>
  )
})